import { atom } from "jotai";
import { atomWithReset } from "jotai/utils";
import { getDay } from "date-fns";
import { addMonths, addYears, format } from "date-fns";
import { dayAbbreviations } from "../utils/Constants";
import {
    convertRRuleFromRFC5545,
    FREQ_MONTHLY,
    FREQ_WEEKLY,
    FREQ_YEARLY
} from "../modules/RecurringBookingsUtilityFunctions";
import { rrule, RRule } from "rrule";

export const days = [
    { id: 0, title: "S" },
    { id: 1, title: "M" },
    { id: 2, title: "T" },
    { id: 3, title: "W" },
    { id: 4, title: "T" },
    { id: 5, title: "F" },
    { id: 6, title: "S" }
];
export const daysMonFirst = [
    { id: 1, title: "M" },
    { id: 2, title: "T" },
    { id: 3, title: "W" },
    { id: 4, title: "T" },
    { id: 5, title: "F" },
    { id: 6, title: "S" },
    { id: 0, title: "S" }
];
export const dayMap = {
    MO: "Monday",
    TU: "Tuesday",
    WE: "Wednesday",
    TH: "Thursday",
    FR: "Friday",
    SA: "Saturday",
    SU: "Sunday"
};
export const RECURRING_PATTERN_TOTAL = "total";
export const RECURRING_PATTERN_INDEF = "indef";
export const RECURRING_PATTERN_UNTIL = "until";
export const recurringSettingsAtom = atomWithReset({
    interval: 1,
    frequency: FREQ_WEEKLY,
    recurringPattern: RECURRING_PATTERN_INDEF,
    totalSessionsNumber: 10,
    cronologicalOrCalendarDay: "CRONO",
    untilDate: null,
    recurringWeekDays: []
});

export const originalTotalNumberOfSessionsAtom = atom(0);
export const hasTotalNumberOfSessionsChangedAtom = atom(
    (get) =>
        get(originalTotalNumberOfSessionsAtom) !==
        get(recurringSettingsAtom).totalSessionsNumber
);
export const setRecurringSettingsFromRruleAtom = atom(
    null,
    (get, set, rrule) => {
        const components = rrule.split(";");
        const parsedRule = {
            frequency: "",
            interval: 1,
            recurringWeekDays: [],
            untilDate: null,
            totalSessionsNumber: 10,
            recurringPattern: RECURRING_PATTERN_INDEF
        };
        const dayMappings = {
            MO: 1,
            TU: 2,
            WE: 3,
            TH: 4,
            FR: 5,
            SA: 6,
            SU: 0
        };
        components.forEach((component) => {
            const [key, value] = component.split("=");
            switch (key) {
                case "FREQ":
                    if (value === "WEEKLY") {
                        parsedRule.frequency = FREQ_WEEKLY;
                    } else if (value === "MONTHLY") {
                        parsedRule.frequency = FREQ_MONTHLY;
                    } else if (value === "YEARLY") {
                        parsedRule.frequency = FREQ_YEARLY;
                    }
                    break;
                case "INTERVAL":
                    parsedRule.interval = parseInt(value, 10);
                    break;
                case "BYDAY":
                    if (value) {
                        if (value === "NUMBER" || value === "WEEK") {
                            parsedRule.recurringWeekDays = value;
                            if (value === "NUMBER")
                                parsedRule.cronologicalOrCalendarDay = "CRONO";
                            if (value === "WEEK")
                                parsedRule.cronologicalOrCalendarDay =
                                    "CALENDAR";
                            break;
                        }
                        const daysArray = value.split(",");
                        daysArray.forEach((day) => {
                            parsedRule.recurringWeekDays.push(dayMappings[day]);
                        });
                        //sort parsedRule.days in ascending order
                        parsedRule.recurringWeekDays.sort((a, b) => a - b);
                    }
                    break;
                case "UNTIL":
                    parsedRule.recurringPattern = RECURRING_PATTERN_UNTIL;
                    parsedRule.untilDate = new Date(`${value}T23:59:59Z`);
                    break;
                case "XOCCUR":
                    parsedRule.recurringPattern = RECURRING_PATTERN_TOTAL;
                    parsedRule.totalSessionsNumber = value;
                    set(originalTotalNumberOfSessionsAtom, value);
                    break;
                default:
                    break;
            }
        });
        set(setRecurringDetailAtom, {
            ...parsedRule
        });
    }
);

export const setRecurringWeekDaysAtom = atom(
    null,
    (get, set, selectedDateTime) => {
        const currentRecurringSettings = get(recurringSettingsAtom);

        // Get the current recurringWeekDays array
        const currentRecurringWeekDays =
            currentRecurringSettings.recurringWeekDays;

        const newDay = getDay(selectedDateTime);

        // Update the recurringWeekDays array with the new day, ensuring no duplicates
        set(recurringSettingsAtom, {
            ...currentRecurringSettings,
            recurringWeekDays: [
                ...new Set([...currentRecurringWeekDays, newDay])
            ]
        });
    }
);

export const setRecurringDetailAtom = atom(null, (get, set, newDetail) => {
    let newSate = { ...newDetail };
    const detailName = Object.keys(newDetail)[0];
    const detailValue = newDetail[detailName];
    const currentState = get(recurringSettingsAtom);
    const frequency = get(recurringSettingsAtom).frequency;

    switch (detailName) {
        case "recurringPattern":
            switch (detailValue) {
                case RECURRING_PATTERN_TOTAL:
                    newSate.recurringPattern = detailValue;
                    newSate.untilDate = "";
                    newSate.totalSessionsNumber =
                        currentState.totalSessionsNumber || 10;
                    break;
                case RECURRING_PATTERN_UNTIL:
                    newSate.recurringPattern = detailValue;
                    newSate.untilDate =
                        frequency === "YEARLY"
                            ? addYears(new Date(), 5)
                            : addMonths(new Date(), 6);
                    newSate.totalSessionsNumber = 10;
                    break;
                case RECURRING_PATTERN_INDEF:
                    newSate.recurringPattern = detailValue;
                    newSate.untilDate = "";
                    newSate.totalSessionsNumber = 10;
                    break;
                default:
                    break;
            }
            break;
        case "frequency":
            break;
        default:
            break;
    }
    set(recurringSettingsAtom, { ...currentState, ...newSate });
});

export const setWeekdaysAtom = atom(null, (get, set, newWeekdays) => {
    set(weekdaysAtom, newWeekdays);
});

export const rruleAtom = atom((get) => {
    const {
        chronologicalSelector,
        interval,
        frequency,
        recurringPattern,
        untilDate,
        totalSessionsNumber,
        recurringWeekDays,
        cronologicalOrCalendarDay
    } = get(recurringSettingsAtom);

    let days;

    if (frequency === FREQ_WEEKLY) {
        // Map the selected day numbers to day abbreviations
        const selectedDayAbbreviations = recurringWeekDays
            .sort((a, b) => a - b)
            .map((dayNumber) => dayAbbreviations[dayNumber]);
        days = selectedDayAbbreviations.join(",");
    } else if (frequency === FREQ_MONTHLY) {
        if (cronologicalOrCalendarDay === "CRONO") days = "NUMBER";
        else days = "WEEK";
        //days = recurringWeekDays;
    } else if (frequency === FREQ_YEARLY) {
        // days = recurringWeekDays;
        if (cronologicalOrCalendarDay === "CRONO") days = "NUMBER";
        else days = "WEEK";
    }
    // Set the until date, and occurrences
    const endDateStr =
        totalSessionsNumber && recurringPattern === RECURRING_PATTERN_TOTAL
            ? `;UNTIL=2099-12-31;XOCCUR=${totalSessionsNumber}`
            : recurringPattern === RECURRING_PATTERN_UNTIL && untilDate
              ? `;UNTIL=${format(new Date(untilDate), "yyyy-MM-dd")}`
              : "";

    return `FREQ=${frequency};INTERVAL=${interval};BYDAY=${days}${endDateStr}`;
});

// RFC rrule default values
const DEFAULT_START_DATE = new Date();
const DEFAULT_FREQUENCY = "WEEKLY";
const DEFAULT_INTERVAL = 1;
const DEFAULT_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];
const DEFAULT_MONTHLY_MODE = "BYDAY";
const DEFAULT_YEARLY_MODE = "BYDAY";
const DEFAULT_COUNT = 1;
const DEFAULT_END_MODE = "indefinitely";
const DEFAULT_END_DATE = new Date(
    new Date().setMonth(new Date().getMonth() + 3)
);

//RFC rrule Atoms
export const startDateAtom = atom(DEFAULT_START_DATE);
export const frequencyAtom = atom(DEFAULT_FREQUENCY);
export const intervalAtom = atom(DEFAULT_INTERVAL);
export const weekdaysAtom = atom(DEFAULT_WEEKDAYS);
export const monthlyModeAtom = atom(DEFAULT_MONTHLY_MODE);
export const yearlyModeAtom = atom(DEFAULT_YEARLY_MODE);
export const countAtom = atom(DEFAULT_COUNT);
export const endModeAtom = atom(DEFAULT_END_MODE);
export const endDateAtom = atom(DEFAULT_END_DATE);

// Reset utility atom
export const resetRecurringSettingsAtom = atom(null, (get, set) => {
    set(startDateAtom, DEFAULT_START_DATE);
    set(frequencyAtom, DEFAULT_FREQUENCY);
    set(intervalAtom, DEFAULT_INTERVAL);
    set(weekdaysAtom, DEFAULT_WEEKDAYS);
    set(monthlyModeAtom, DEFAULT_MONTHLY_MODE);
    set(yearlyModeAtom, DEFAULT_YEARLY_MODE);
    set(countAtom, DEFAULT_COUNT);
    set(endModeAtom, DEFAULT_END_MODE);
    set(endDateAtom, DEFAULT_END_DATE);
});

// Derived atom for the RFC rrule string
export const rfcRRuleAtom = atom((get) => {
    const startDate = get(startDateAtom);
    const frequency = get(frequencyAtom);
    const interval = get(intervalAtom);
    const weekdays = get(weekdaysAtom);
    const monthlyMode = get(monthlyModeAtom);
    const endMode = get(endModeAtom);
    const endDate = get(endDateAtom);
    const count = get(countAtom);

    const parts = [`FREQ=${frequency}`, `INTERVAL=${interval}`];

    if (frequency === "YEARLY") {
        const month = startDate.getMonth() + 1;
        parts.push(`BYMONTH=${month}`);
    }
    if (frequency === "WEEKLY" && weekdays.length > 0) {
        const dayAbbrs = weekdays.map((day) => {
            const days = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
            return days[day];
        });
        parts.push(`BYDAY=${dayAbbrs.join(",")}`);
    }

    if (frequency === "MONTHLY") {
        if (monthlyMode === "BYDAY") {
            const dayOfWeek = startDate.getDay(); // 0 (Sun) to 6 (Sat)
            const days = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
            const weekNumber = Math.ceil(startDate.getDate() / 7);
            parts.push(`BYDAY=${weekNumber}${days[dayOfWeek]}`);
        } else {
            parts.push(`BYMONTHDAY=${startDate.getDate()}`);
        }
    }

    if (frequency === "YEARLY") {
        const dayOfWeek = startDate.getDay(); // 0 (Sun) to 6 (Sat)
        const days = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
        const weekNumber = Math.ceil(startDate.getDate() / 7);
        parts.push(`BYDAY=${weekNumber}${days[dayOfWeek]}`);
    }

    if (endMode === "endDate" && endDate) {
        //YYYYMMDD (RFC 5545 format)
        const untilStr = endDate.toISOString().split("T")[0].replace(/-/g, "");
        parts.push(`UNTIL=${untilStr}`);
    } else if (endMode === "count" && count !== null) {
        parts.push(`COUNT=${count}`);
    }

    return parts.join(";");
});

// Derived atom that performs the conversion and holds the output/error
export const mbRecurringBookingRRuleAtom = atom((get) => {
    const rfcRRuleStr = get(rfcRRuleAtom);

    return convertRRuleFromRFC5545(rfcRRuleStr) || "";
});
